import is from '@sindresorhus/is';
import { Message } from '@sr/types';
import { useEffect } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { useIsProUser } from './use-user';
import useStore from 'store';
import { ref } from 'v1/utils/firebase';

export const useMessages = (userId: string, resumeId: string) => {
  const initializeMessages = useStore.useInitializeMessages();
  const deleteMessage = useStore.useDeleteMessage();
  const isPro = useIsProUser();
  const setIsPro = useStore.useSetIsPro();
  const setMessage = useStore.useSetMessage();

  // Track the user's pro status in the store
  useEffect(() => {
    setIsPro(!!isPro);
  }, [isPro, setIsPro]);

  useEffect(() => {
    const messagesRef = ref.child(`reviews/${userId}/${resumeId}/messages`);

    // Fetch all messages once to start. This allows us to have an initial load state.
    messagesRef.once('value', (snap) => {
      const data = snap.val();
      let messages: Message[] = [];
      // Handle when the server returns null
      if (is.object(data)) {
        messages = [...Object.values(data)];
      }
      // batchedUpdates are needed when an action is called outside of a react
      // event to avoid zombie children. Callbacks like this are considered outside.
      batchedUpdates(() => {
        initializeMessages(messages);
      });
    });

    messagesRef.on('child_added', (snap) => {
      const message: Message = snap.val();
      batchedUpdates(() => {
        setMessage(message);
      });
    });

    messagesRef.on('child_removed', (snap) => {
      const message: Message = snap.val();
      batchedUpdates(() => {
        deleteMessage(message);
      });
    });

    messagesRef.on('child_changed', (snap) => {
      const message: Message = snap.val();
      batchedUpdates(() => {
        setMessage(message);
      });
    });

    return () => {
      // Cleanup Firebase event listeners
      messagesRef.off('child_added');
      messagesRef.off('child_removed');
      messagesRef.off('child_changed');
    };
  }, [deleteMessage, initializeMessages, resumeId, setMessage, userId]);
};
